import { CareArticle } from './careArticles.types'
import { CourseInTimelineItem } from './courses.types'
import { LearnArticle } from './learnArticles.types'
import { Lesson } from './lesson.types'
import { Note } from './note.types'
import { Query } from './query.types'
import { Questionnaire } from './questionnaire.types'
import { QuizResultData } from './quiz.types'
import { User, UserUpdater } from './user.types'

export type QuestioningType = 'quiz' | 'query' | 'questionnaire'

export enum ETimelinePostType {
  All = 'All',
  careArticle = 'careArticle',
  taskArticle = 'taskArticle',
  lessons = 'lesson',
  note = 'note',
  learnArticles = 'learnArticle',
  queries = 'query',
  eventOnDate = 'eventOnDate',
  questionnaire = 'questionnaire',
  course = 'course',
}

export enum ETimelinePostStatus {
  new = 'new',
  review = 'review',
  retake = 'retake',
  done = 'done',
}

export const allFiltersStatuses = [
  ETimelinePostStatus.new,
  ETimelinePostStatus.review,
  ETimelinePostStatus.done,
]

export interface BaseTimelineItem {
  id: number
  type: ETimelinePostType
  status: ETimelinePostStatus
  userId: number
  user: User
  course: CourseInTimelineItem | null
  updatedAt: string
}

export interface EventTimelineItem {
  id: string
  type: ETimelinePostType.eventOnDate
  date: string
  text: string
}

export interface CareTimelineItem extends BaseTimelineItem {
  type: ETimelinePostType.careArticle
  careArticleId: number
  careArticle: CareArticle
}

export interface TaskTimelineItem extends BaseTimelineItem {
  type: ETimelinePostType.taskArticle
  careArticleId: number
  careArticle: CareArticle
}

export interface LessonTimelineItem extends BaseTimelineItem {
  type: ETimelinePostType.lessons
  lessonId: number
  lesson: Lesson & {
    completedLessons?: QuizResultData[]
  }
}

interface NoteTimelineItem extends BaseTimelineItem {
  type: ETimelinePostType.note
  noteId: number
  note: Note
}

interface QueryTimelineItem extends BaseTimelineItem {
  type: ETimelinePostType.queries
  queryId: number
  query: Query
}

export interface QuestionnaireTimelineItem extends BaseTimelineItem {
  type: ETimelinePostType.questionnaire
  questionnaireId: number
  questionnaire: Questionnaire
}

export interface CourseTimelineItem {
  id: number
  type: ETimelinePostType.course
  name: string
  status: ETimelinePostStatus
  bannerUrl: string | null
  items: (LessonTimelineItem | QuestionnaireTimelineItem)[]
  updatedAt: string
}

export type TimelineItem =
  | CareTimelineItem
  | TaskTimelineItem
  | LessonTimelineItem
  | NoteTimelineItem
  | QueryTimelineItem
  | QuestionnaireTimelineItem

export type TimeLineWithoutEvents = Exclude<TimelineItem, EventTimelineItem>

export interface UpdateTimelineItem {
  id: number
  status: ETimelinePostStatus
  type: ETimelinePostType
}

export interface TimelineSearchResult {
  itemType: ETimelinePostType
  itemId: number
  post: string
  timelineId: number | null
  createdAt: string
  updatedAt: string
  status: ETimelinePostStatus
}

export interface TimelineEnhancedSearchResultItem {
  id: number
  contentType: ETimelinePostType
  title: string
  content: string
  titleMatch: string
  relevanceScore: number
  matchType: string
  createdAt: string
  updatedAt: string
  contentMatch: string
}

export interface TimelineEnhancedSearchResult {
  contentMatches: number
  hasMoreResults: boolean
  query: {
    original: string
    processed: string
    keywords: string[]
  }
  results: TimelineEnhancedSearchResultItem[]
  suggestions: { topics: string[] }
  titleMatches: number
  totalCount: number
}

export enum ETimelineSearchType {
  text = 'text',
  tag = 'tag',
}

export interface LearnArticleTimelineItem extends BaseTimelineItem {
  type: ETimelinePostType.learnArticles
  learnArticleId: number
  learnArticle: LearnArticle
}

export type ShareTimelineItem = {
  id: number
  senderId: number
  userId: number
  type: ETimelinePostType
  status: ETimelinePostStatus
  careTeamId: number
  comment: string
  createdAt: string | null
  updatedAt: string | null
  user: UserUpdater
  sender: UserUpdater
  lessonId: number | null
  lesson: Lesson | null
  careArticleId: number | null
  careArticle: CareArticle | null
  noteId: number | null
  note: Note | null
  learnArticleId: number | null
  learnArticle: LearnArticle | null
}

export interface RequestUpdateSharedItemStatus {
  entityId: number
  status: ETimelinePostStatus
  type: ETimelinePostType
}
